/**
 * Projected climate for an area, and the heat island under a point.
 *
 * Pure: no React, no fetching. Types mirror `api/mete_api/routers/climate.py`.
 *
 * The load-bearing idea here is that these numbers are **scenarios, not
 * forecasts**. RCP 8.5 is not a prediction; it is what happens if emissions
 * keep rising, and RCP 2.6 is what happens if they fall fast. Neither is
 * likely in the sense a weather forecast is likely. Everything below therefore
 * returns a *range across the scenarios* wherever it can, and the panel draws
 * that range rather than a line.
 */

export type Horizon = "REF" | "H1" | "H2" | "H3";

export const HORIZON_YEARS: Record<Horizon, { label: string; mid: number }> = {
  REF: { label: "1976–2005", mid: 1990 },
  H1: { label: "2021–2050", mid: 2035 },
  H2: { label: "2041–2070", mid: 2055 },
  H3: { label: "2071–2100", mid: 2085 },
};

export const SCENARIO_LABELS: Record<string, string> = {
  "RCP2.6": "Emissions fall fast",
  "RCP4.5": "Emissions peak, then fall",
  "RCP8.5": "Emissions keep rising",
};

export interface ClimateRow {
  scenario: string;
  horizon: Horizon;
  mean_temp: number;
  mean_tmax: number;
  summer_days: number;
  hot_days: number;
  tropical_nights: number;
  heatwave_days: number;
  frost_days: number;
  cooling_dd: number;
  heating_dd: number;
  rain_mm: number;
  rain_days: number;
  heavy_rain_days: number;
  dry_spell_days: number;
  intense_rain_pct: number;
}

export type IndicatorId = keyof Omit<ClimateRow, "scenario" | "horizon">;

export interface AreaClimate {
  scope: string;
  code: string;
  resolved_from: string | null;
  exact: boolean;
  /** Model cell size in metres. Stated, not implied. */
  resolution_m: number;
  /** How far this area sits from the centre of the cell describing it. */
  grid_distance_m: number | null;
  horizons: Record<string, string>;
  series: ClimateRow[];
}

export interface HeatIsland {
  day_class: number;
  night_class: number;
  day_score: number;
  night_score: number;
  lcz: string | null;
  type_lcz: string | null;
  built_pct: number | null;
  permeable_pct: number | null;
  albedo: number | null;
}

export interface WaterRestriction {
  zone: string | null;
  /** Which resource is restricted, in words: surface water, groundwater… */
  type: string | null;
  level: string | null;
  /** How many of the listed usages bind a private household. */
  household_restrictions: number;
}

/** The four official levels, plain-English. */
export const WATER_LEVELS: Record<string, string> = {
  vigilance: "Vigilance",
  alerte: "Alert",
  alerte_renforcee: "Reinforced alert",
  crise: "Crisis",
};

export interface PointClimate {
  lon: number;
  lat: number;
  heat_island: HeatIsland | null;
  /** `null` means VigiEau was unreachable, not that nothing is in force. */
  water_restriction: WaterRestriction | null;
}

export interface Indicator {
  label: string;
  unit: string;
  /** Short unit for a figure tile, where the label carries the rest. */
  short: string;
  format: (v: number) => string;
  /** Why a buyer should care, in one line. Empty where it is self-evident. */
  meaning: string;
}

export const INDICATORS: Record<IndicatorId, Indicator> = {
  mean_temp: {
    label: "Mean temperature",
    unit: "°C",
    short: "°C",
    format: (v) => v.toFixed(1),
    meaning:
      "The headline number, and the least useful one — the extremes below are what you feel.",
  },
  mean_tmax: {
    label: "Mean daily high",
    unit: "°C",
    short: "°C",
    format: (v) => v.toFixed(1),
    meaning: "",
  },
  tropical_nights: {
    label: "Tropical nights",
    unit: "nights a year above 20 °C",
    short: "nights",
    format: (v) => String(Math.round(v)),
    meaning:
      "What decides whether a top-floor flat is liveable in August, and what no fan fixes.",
  },
  heatwave_days: {
    label: "Heatwave days",
    unit: "days a year",
    short: "days",
    format: (v) => String(Math.round(v)),
    meaning: "Days inside a spell the model counts as a heatwave.",
  },
  summer_days: {
    label: "Summer days",
    unit: "days a year above 25 °C",
    short: "days",
    format: (v) => String(Math.round(v)),
    meaning: "",
  },
  hot_days: {
    label: "Abnormally hot days",
    unit: "days a year",
    short: "days",
    format: (v) => String(Math.round(v)),
    meaning: "",
  },
  frost_days: {
    label: "Frost days",
    unit: "days a year",
    short: "days",
    format: (v) => String(Math.round(v)),
    meaning:
      "Falling frost counts cut heating, which is the one line of this that moves your way.",
  },
  cooling_dd: {
    label: "Cooling demand",
    unit: "degree-days a year",
    short: "DD",
    format: (v) => String(Math.round(v)),
    meaning: "The direct link to a running cost: cooling is billed by degree-days.",
  },
  heating_dd: {
    label: "Heating demand",
    unit: "degree-days a year",
    short: "DD",
    format: (v) => String(Math.round(v)),
    meaning:
      "Falls as the climate warms, and in this region by more than cooling rises.",
  },
  rain_mm: {
    label: "Annual rainfall",
    unit: "mm",
    short: "mm",
    format: (v) => String(Math.round(v)),
    meaning: "",
  },
  rain_days: {
    label: "Rain days",
    unit: "days a year",
    short: "days",
    format: (v) => String(Math.round(v)),
    meaning: "",
  },
  heavy_rain_days: {
    label: "Heavy rain days",
    unit: "days a year above 20 mm",
    short: "days",
    format: (v) => String(Math.round(v)),
    meaning: "Rain arriving faster than drains take it — the flash-flood and mudflow side.",
  },
  dry_spell_days: {
    label: "Longest dry spell",
    unit: "consecutive days",
    short: "days",
    format: (v) => String(Math.round(v)),
    meaning:
      "Long dry spells shrink clay soils, so this compounds with the clay under the building.",
  },
  intense_rain_pct: {
    label: "Rain falling in intense days",
    unit: "%",
    short: "%",
    format: (v) => `${Math.round(v)}%`,
    meaning: "",
  },
};

export function rowFor(
  series: ClimateRow[],
  scenario: string,
  horizon: Horizon,
): ClimateRow | undefined {
  if (horizon === "REF") return series.find((r) => r.horizon === "REF");
  return series.find((r) => r.scenario === scenario && r.horizon === horizon);
}

export function reference(series: ClimateRow[]): ClimateRow | undefined {
  return series.find((r) => r.horizon === "REF");
}

export interface TrajectoryPoint {
  year: number;
  label: string;
  /** The chosen scenario's value. */
  value: number | null;
  /** Lowest and highest across all scenarios at this horizon — the spread. */
  low: number | null;
  high: number | null;
  /** The reference period, carried separately so the chart can anchor on it. */
  observed: number | null;
}

/**
 * One indicator from the reference period out to 2100, with the spread across
 * every scenario as a band.
 *
 * The band is the point. A single line labelled "RCP4.5" reads as a forecast;
 * the same line inside the range the other pathways give reads as what it is —
 * one choice among several, and the choice is not the model's to make.
 */
export function trajectory(
  series: ClimateRow[],
  indicator: IndicatorId,
  scenario: string,
): TrajectoryPoint[] {
  const ref = reference(series);
  const refValue = ref ? Number(ref[indicator]) : null;

  const points: TrajectoryPoint[] = [
    {
      year: HORIZON_YEARS.REF.mid,
      label: HORIZON_YEARS.REF.label,
      value: refValue,
      low: refValue,
      high: refValue,
      observed: refValue,
    },
  ];

  for (const horizon of ["H1", "H2", "H3"] as Horizon[]) {
    const atHorizon = series.filter((r) => r.horizon === horizon);
    if (atHorizon.length === 0) continue;
    const values = atHorizon.map((r) => Number(r[indicator])).filter(Number.isFinite);
    const chosen = atHorizon.find((r) => r.scenario === scenario);
    points.push({
      year: HORIZON_YEARS[horizon].mid,
      label: HORIZON_YEARS[horizon].label,
      value: chosen ? Number(chosen[indicator]) : null,
      low: values.length ? Math.min(...values) : null,
      high: values.length ? Math.max(...values) : null,
      observed: null,
    });
  }
  return points;
}

/** Change from the reference period to one horizon, under one scenario. */
export function changeFromReference(
  series: ClimateRow[],
  indicator: IndicatorId,
  scenario: string,
  horizon: Horizon,
): { from: number; to: number; delta: number } | null {
  const ref = reference(series);
  const row = rowFor(series, scenario, horizon);
  if (!ref || !row) return null;
  const from = Number(ref[indicator]);
  const to = Number(row[indicator]);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  return { from, to, delta: to - from };
}

export const HEAT_LABELS: Record<number, string> = {
  [-1]: "Not assessed",
  1: "Low",
  2: "Moderate",
  3: "Strong",
};

/**
 * How the block's own form explains its heat, as a phrase.
 *
 * The dataset carries the drivers alongside the class — how much is built, how
 * much ground water can soak into, how dark the surfaces are — and naming which
 * one is doing the work beats the class alone, because some of them a
 * copropriété can actually change.
 */
export function heatDrivers(icu: HeatIsland): string | null {
  const parts: string[] = [];
  if (icu.built_pct != null && icu.built_pct >= 40) {
    parts.push(`${Math.round(icu.built_pct)}% built`);
  }
  if (icu.permeable_pct != null && icu.permeable_pct <= 20) {
    parts.push(`only ${Math.round(icu.permeable_pct)}% permeable ground`);
  }
  if (icu.albedo != null && icu.albedo <= 0.15) {
    parts.push("dark surfaces");
  }
  return parts.length ? parts.join(", ") : null;
}


export interface Fire {
  year: number;
  ha: number;
  started: string;
  cause: string | null;
}

export interface AreaFires {
  scope: string;
  code: string;
  resolved_from: string | null;
  exact: boolean;
  /** Every ignition BDIFF holds, however small. */
  recorded: number;
  /** Only those of a hectare or more. */
  significant: number;
  hectares: number;
  largest_ha: number;
  last_year: number | null;
  worst: Fire[];
  by_year: { year: number; n: number; ha: number }[];
}

/** The line between a fire and an incident, matching the ETL's own threshold. */
export const FIRE_SIGNIFICANT_HA = 1;

/** BDIFF went national in 2006; before that it covered only the Mediterranean. */
export const FIRE_FIRST_YEAR = 2006;

export function formatHectares(ha: number): string {
  if (ha >= 10) return `${Math.round(ha)} ha`;
  if (ha >= 1) return `${ha.toFixed(1)} ha`;
  if (ha >= 0.01) return `${Math.round(ha * 10000)} m²`;
  return "<100 m²";
}
